import type {
  ProjectInvoice,
  ProjectInvoicePayment,
  ProjectInvoicePendingPaymentRequest,
} from '@services/projectService';
import type { AppliedInvoiceDiscount, PaymentPlanInstallment } from '@/types/invoicing';

// Optional project context used to backfill invoice fields that the API omits.
export interface InvoiceProjectContext {
  id: string;
  name: string;
  contactName?: string;
  contactEmail?: string;
}

export const parseCurrencyString = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return 0;
  const parsed = Number.parseFloat(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const normalizeStatus = (status: unknown): ProjectInvoice['status'] => {
  switch (status) {
    case 'draft':
    case 'sent':
    case 'paid':
    case 'overdue':
    case 'cancelled':
      return status;
    case 'open':
    case 'unpaid':
      return 'sent';
    case 'in-progress':
      return 'draft';
    case 'closed':
      return 'cancelled';
    default:
      return 'draft';
  }
};

const normalizePaymentMethod = (method: unknown): ProjectInvoicePayment['method'] => {
  switch (method) {
    case 'cash':
    case 'card':
    case 'transfer':
    case 'other':
      return method;
    default:
      return 'other';
  }
};

export const normalizePayments = (payments: unknown): ProjectInvoicePayment[] => {
  if (!Array.isArray(payments)) return [];

  return payments.reduce<ProjectInvoicePayment[]>((normalizedPayments, payment, index) => {
    if (!payment || typeof payment !== 'object') return normalizedPayments;
    const entry = payment as Record<string, unknown>;
    const amount =
      typeof entry.amount === 'number' ? entry.amount : parseCurrencyString(entry.amount);

    normalizedPayments.push({
      id: String(entry.id ?? `payment-${index}`),
      amount,
      method: normalizePaymentMethod(entry.method),
      note: typeof entry.note === 'string' ? entry.note : undefined,
      paidAt: typeof entry.paidAt === 'string' ? entry.paidAt : new Date().toISOString(),
    } satisfies ProjectInvoicePayment);

    return normalizedPayments;
  }, []);
};

export const normalizePendingPaymentRequests = (
  requests: unknown
): ProjectInvoicePendingPaymentRequest[] => {
  if (!Array.isArray(requests)) return [];

  return requests.reduce<ProjectInvoicePendingPaymentRequest[]>(
    (normalizedRequests, request, index) => {
      if (!request || typeof request !== 'object') return normalizedRequests;
      const entry = request as Record<string, unknown>;
      const method = entry.method === 'card' ? 'card' : 'transfer';
      const status =
        entry.status === 'approved' || entry.status === 'declined' ? entry.status : 'pending';

      normalizedRequests.push({
        id: String(entry.id ?? `payment-request-${index}`),
        amount: typeof entry.amount === 'number' ? entry.amount : parseCurrencyString(entry.amount),
        method,
        note: typeof entry.note === 'string' ? entry.note : undefined,
        paidAt: typeof entry.paidAt === 'string' ? entry.paidAt : new Date().toISOString(),
        submittedAt:
          typeof entry.submittedAt === 'string' ? entry.submittedAt : new Date().toISOString(),
        status,
        clientName: typeof entry.clientName === 'string' ? entry.clientName : undefined,
        clientEmail: typeof entry.clientEmail === 'string' ? entry.clientEmail : undefined,
        source: entry.source === 'manual' ? 'manual' : 'client_portal',
      });

      return normalizedRequests;
    },
    []
  );
};

const getRawInvoiceItems = (rawInvoice: Record<string, unknown>) => {
  if (Array.isArray(rawInvoice.items)) return rawInvoice.items;
  if (Array.isArray(rawInvoice.lineItems)) return rawInvoice.lineItems;
  return [];
};

const getInvoiceItemUnitPrice = (entry: Record<string, unknown>): number => {
  if (typeof entry.unitPrice === 'number') return entry.unitPrice;
  if (typeof entry.price === 'number') return entry.price;
  return parseCurrencyString(entry.unitPrice ?? entry.price);
};

const getInvoiceAmount = (rawInvoice: Record<string, unknown>, computedAmount: number) => {
  if (typeof rawInvoice.amount === 'number') return rawInvoice.amount;
  return computedAmount || parseCurrencyString(rawInvoice.total);
};

const getInvoiceDate = (rawInvoice: Record<string, unknown>) => {
  if (typeof rawInvoice.invoiceDate === 'string') return rawInvoice.invoiceDate;
  if (typeof rawInvoice.createdAt === 'string') return rawInvoice.createdAt;
  return new Date().toISOString();
};

const getInvoiceNotes = (rawInvoice: Record<string, unknown>) => {
  if (typeof rawInvoice.notes === 'string') return rawInvoice.notes;
  if (typeof rawInvoice.note === 'string') return rawInvoice.note;
  return undefined;
};

const normalizeAppliedDiscounts = (value: unknown): AppliedInvoiceDiscount[] => {
  if (!Array.isArray(value)) return [];

  return value.reduce<AppliedInvoiceDiscount[]>((discounts, entry) => {
    if (!entry || typeof entry !== 'object') return discounts;
    const record = entry as Record<string, unknown>;
    const type = record.type === '%' ? '%' : '$';

    discounts.push({
      id: String(record.id ?? `discount-${discounts.length}`),
      name: String(record.name ?? 'Discount'),
      amount: parseCurrencyString(record.amount),
      type,
    });

    return discounts;
  }, []);
};

const normalizePaymentPlan = (value: unknown): PaymentPlanInstallment[] => {
  if (!Array.isArray(value)) return [];

  return value.reduce<PaymentPlanInstallment[]>((installments, entry, index) => {
    if (!entry || typeof entry !== 'object') return installments;
    const record = entry as Record<string, unknown>;
    const rawStatus = record.status;
    const status = rawStatus === 'paid' || rawStatus === 'unpaid' ? rawStatus : 'in-progress';

    installments.push({
      id: String(record.id ?? `installment-${index}`),
      label: String(record.label ?? `Payment ${index + 1}`),
      dueDate: String(record.dueDate ?? ''),
      amount: parseCurrencyString(record.amount),
      status,
    });

    return installments;
  }, []);
};

// Coerces a raw API invoice payload into the canonical ProjectInvoice shape.
export const normalizeInvoice = (
  rawInvoice: Record<string, unknown>,
  project?: InvoiceProjectContext
): ProjectInvoice => {
  const rawItems = getRawInvoiceItems(rawInvoice);

  const items = rawItems.map((item, index) => {
    const entry = item as Record<string, unknown>;
    return {
      id: String(entry.id ?? `item-${index}`),
      description: String(entry.description ?? entry.name ?? 'Untitled Item'),
      quantity: typeof entry.quantity === 'number' ? entry.quantity : 1,
      unitPrice: getInvoiceItemUnitPrice(entry),
    };
  });

  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const taxRate = typeof rawInvoice.taxRate === 'number' ? rawInvoice.taxRate : 0;
  const discount = typeof rawInvoice.discount === 'number' ? rawInvoice.discount : 0;
  const computedAmount = subtotal + subtotal * (taxRate / 100) - discount;

  return {
    id: String(rawInvoice.id ?? ''),
    projectId: String(rawInvoice.projectId ?? project?.id ?? ''),
    invoiceNumber: String(rawInvoice.invoiceNumber ?? rawInvoice.number ?? `${Date.now()}`),
    projectName: String(
      rawInvoice.projectName ?? rawInvoice.project ?? rawInvoice.title ?? project?.name ?? ''
    ),
    contactName: String(rawInvoice.contactName ?? rawInvoice.contact ?? project?.contactName ?? ''),
    contactEmail: String(rawInvoice.contactEmail ?? project?.contactEmail ?? ''),
    amount: getInvoiceAmount(rawInvoice, computedAmount),
    currency: String(rawInvoice.currency ?? 'CAD'),
    status: normalizeStatus(rawInvoice.status),
    dueDate: typeof rawInvoice.dueDate === 'string' ? rawInvoice.dueDate : undefined,
    invoiceDate: getInvoiceDate(rawInvoice),
    paidAt: typeof rawInvoice.paidAt === 'string' ? rawInvoice.paidAt : undefined,
    payments: normalizePayments(rawInvoice.payments),
    pendingPaymentRequests: normalizePendingPaymentRequests(rawInvoice.pendingPaymentRequests),
    items,
    notes: getInvoiceNotes(rawInvoice),
    paymentTerms: typeof rawInvoice.paymentTerms === 'string' ? rawInvoice.paymentTerms : undefined,
    taxRate,
    discount,
    appliedDiscounts: normalizeAppliedDiscounts(rawInvoice.appliedDiscounts),
    paymentScheduleId:
      typeof rawInvoice.paymentScheduleId === 'string' ? rawInvoice.paymentScheduleId : undefined,
    paymentPlan: normalizePaymentPlan(rawInvoice.paymentPlan),
    invoiceShareToken:
      typeof rawInvoice.invoiceShareToken === 'string' ? rawInvoice.invoiceShareToken : undefined,
    portalShareToken:
      typeof rawInvoice.portalShareToken === 'string' ? rawInvoice.portalShareToken : undefined,
    createdAt:
      typeof rawInvoice.createdAt === 'string' ? rawInvoice.createdAt : new Date().toISOString(),
    updatedAt:
      typeof rawInvoice.updatedAt === 'string' ? rawInvoice.updatedAt : new Date().toISOString(),
  };
};
